import React, { useEffect, useRef, useMemo, useState } from 'react';
import { createPluginUI } from 'molstar/lib/mol-plugin-ui';
import { renderReact18 } from 'molstar/lib/mol-plugin-ui/react18';
import 'molstar/lib/mol-plugin-ui/skin/light.scss';
import { PluginCommands } from 'molstar/lib/mol-plugin/commands';
import { Script } from 'molstar/lib/mol-script/script';
import { StructureSelection } from 'molstar/lib/mol-model/structure/query';
import { StructureElement, StructureProperties, Unit } from 'molstar/lib/mol-model/structure';
import { MolScriptBuilder } from 'molstar/lib/mol-script/language/builder';
import { StateTransforms } from 'molstar/lib/mol-plugin-state/transforms';
import { createStructureRepresentationParams } from 'molstar/lib/mol-plugin-state/helpers/structure-representation-params';
import { Color } from 'molstar/lib/mol-util/color';
import { CustomInteractions, InteractionsShape } from 'molstar/lib/extensions/interactions/transforms';
import { ShapeRepresentation3D } from 'molstar/lib/mol-plugin-state/transforms/representation';
import { setSubtreeVisibility } from 'molstar/lib/mol-plugin/behavior/static/state';

// ──────────────────────────────────────────────────────────────────────────
// NUMBERING
//
// Three tools read the same file and number residues differently:
//
//     BioPython / 3DContact_hetatm   4PJ1 chain A   1..526   (label_seq_id)
//     Foldseek                       4PJ1 chain A   27..552  (auth_seq_id)
//
// The contact rows and the residue map both use the first. Mol* selections
// and the CustomInteractions transform both speak auth_seq_id, so every
// position coming in from the page is translated through an index built from
// the structure itself. On AlphaFold models the two coincide, which is
// exactly why this would go unnoticed until a crystal structure was uploaded.
// ──────────────────────────────────────────────────────────────────────────

const BASE_COLOUR = Color(0xE9ECF1);

const CLASS_COLOURS = {
    projected_high_confidence: 0x17507E,
    projected_candidate: 0xB9700A,
    human_only: 0x4B5563,
    not_projected: 0x8A94A6
};

// Which class wins when several annotations land on one residue. Human-only
// sits last: a ClinVar variant says something about the human protein, so it
// must not paint over a class that says something about the query.
const CLASS_PRIORITY = [
    'projected_high_confidence',
    'projected_candidate',
    'not_projected',
    'human_only'
];

const MAP_COLOURS = { mapped: 0x17507E, gap: 0xB9700A, unaligned: 0xC7CEDB };

const CONTACT_STEPS = [
    { min: 6, colour: 0x17507E },
    { min: 3, colour: 0x4A7BA8 },
    { min: 1, colour: 0x9BB4CE }
];

const contactColour = (n) => {
    const step = CONTACT_STEPS.find((s) => n >= s.min);
    return step ? step.colour : null;
};

const SELECTED_CONTACT_COLOR = 0xF0E68C;
const SELECTED_CONTACT_RADIUS = 0.11;
const NETWORK_COLOR = 0x9AA5B1;
const NETWORK_RADIUS = 0.05;
const NETWORK_CARBON = 0x8C96A0;

// Mol*'s own viewport buttons sit in a 32px column inset by 10px from the top
// right, so these sit to their left and borrow the light skin's metrics.
const MSP_CONTROL_SPACING = 10;
const MSP_CONTROL_COLUMN = 32;
const MSP_BUTTON_BAR_STYLE = {
    position: 'absolute',
    top: MSP_CONTROL_SPACING,
    right: MSP_CONTROL_SPACING + MSP_CONTROL_COLUMN + 6,
    zIndex: 10,
    display: 'flex',
    gap: 6
};
const MSP_BUTTON_STYLE = {
    height: 32,
    padding: '0 10px',
    border: 'none',
    borderRadius: 0,
    background: '#F3F2EE',
    color: '#332B1F',
    fontFamily: '"Helvetica Neue", "Segoe UI", Helvetica, "Source Sans Pro", Arial, sans-serif',
    fontSize: 14,
    lineHeight: '30px',
    boxShadow: 'none',
    cursor: 'pointer'
};

const toSeqId = (p) => {
    if (typeof p === 'number') return p;
    const m = String(p ?? '').match(/(\d+)\s*$/);
    return m ? parseInt(m[1], 10) : parseInt(String(p ?? ''), 10);
};

// ──────────────────────────────────────────────────────────────────────────
// Structure indices
// ──────────────────────────────────────────────────────────────────────────

// label_seq_id -> auth_seq_id, per chain, plus the atom names of each residue.
// One pass over the structure gives both, and both are needed: the first to
// translate incoming positions, the second so an interaction endpoint never
// resolves to an empty loci (CB on a glycine, CA on a ligand), whose bounding
// sphere is the origin - that is what draws a dash off into empty space.
function buildStructureIndex(structureData) {
    const authByLabel = new Map();   // `chain|label_seq` -> auth_seq
    const atomsByAuth = new Map();   // `chain|auth_seq`  -> Set(atom names)

    if (!structureData) return { authByLabel, atomsByAuth };

    for (const unit of structureData.units) {
        if (!Unit.isAtomic(unit) || unit.elements.length === 0) continue;
        const loc = StructureElement.Location.create(structureData, unit, unit.elements[0]);
        for (let i = 0, il = unit.elements.length; i < il; i++) {
            loc.element = unit.elements[i];
            const chain = StructureProperties.chain.auth_asym_id(loc);
            const authSeq = StructureProperties.residue.auth_seq_id(loc);
            const labelSeq = StructureProperties.residue.label_seq_id(loc);

            authByLabel.set(`${chain}|${labelSeq}`, authSeq);

            const key = `${chain}|${authSeq}`;
            let atoms = atomsByAuth.get(key);
            if (!atoms) { atoms = new Set(); atomsByAuth.set(key, atoms); }
            atoms.add(StructureProperties.atom.label_atom_id(loc));
        }
    }

    return { authByLabel, atomsByAuth };
}

// -> { atom } with the atom to anchor on (undefined = the whole residue, i.e.
// its centroid: the sane fallback for ligands and for GLY without CB), or null
// when the residue is not in this structure at all.
function resolveAnchorAtom(atomsByAuth, chainId, authSeq, preferred) {
    const atoms = atomsByAuth.get(`${chainId}|${authSeq}`);
    if (!atoms) return null;
    for (const candidate of [preferred, 'CB', 'CA']) {
        if (candidate && atoms.has(candidate)) return { atom: candidate };
    }
    return { atom: undefined };
}

// ──────────────────────────────────────────────────────────────────────────
// Colouring
// ──────────────────────────────────────────────────────────────────────────

async function resetCartoonColour(plugin, repRef, colour = BASE_COLOUR) {
    const params = createStructureRepresentationParams(plugin, repRef.obj?.data.sourceData, {
        type: 'cartoon',
        color: 'uniform',
        colorParams: { value: colour }
    });
    await plugin.build().to(repRef).update(params).commit();
}

async function overpaintResidues(plugin, repRef, layersSpec, chain) {
    await resetCartoonColour(plugin, repRef);

    const layers = layersSpec
        .filter((l) => l && l.colour !== null && l.colour !== undefined && l.positions.length)
        .map((l) => ({
            script: Script(
                `(sel.atom.atom-groups
                    :chain-test (= atom.auth_asym_id ${chain || 'A'})
                    :residue-test (set.has (set ${l.positions.join(' ')}) atom.auth_seq_id))`,
                'mol-script'
            ),
            color: Color(l.colour),
            clear: false
        }));

    if (!layers.length) return;

    await plugin.build()
        .to(repRef)
        .apply(StateTransforms.Representation.OverpaintStructureRepresentation3DFromScript, { layers })
        .commit();
}

function residueLabelProvider(labelsByAuthSeq) {
    return {
        label: (loci) => {
            if (!StructureElement.Loci.is(loci)) return undefined;
            const loc = StructureElement.Loci.getFirstLocation(loci);
            if (!loc) return undefined;
            return labelsByAuthSeq.get(StructureProperties.residue.auth_seq_id(loc));
        }
    };
}

// ──────────────────────────────────────────────────────────────────────────

const MolstarViewerProjection = ({
    cifString,
    chain = 'A',
    colourBy = 'class',
    annotations = [],
    mapRuns = [],
    perPosition = {},
    contacts = [],
    selectedContact = null,
    selectedResidue = null
}) => {
    const containerRef = useRef(null);
    const pluginRef = useRef(null);
    const cartoonRef = useRef(null);
    const structureRef = useRef(null);
    const structureDataRef = useRef(null);
    const indexRef = useRef({ authByLabel: new Map(), atomsByAuth: new Map() });
    const labelProviderRef = useRef(null);
    const selectionNodesRef = useRef([]);
    const networkRefs = useRef([]);

    const [ready, setReady] = useState(false);
    const [showNetwork, setShowNetwork] = useState(false);
    const [hasNetwork, setHasNetwork] = useState(false);

    const toAuth = useMemo(() => (labelSeq) => {
        const v = indexRef.current.authByLabel.get(`${chain}|${labelSeq}`);
        return v === undefined ? labelSeq : v;
    }, [chain, ready]);

    // ------------------------------------------------------------ load once
    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            if (!containerRef.current || !cifString) return;

            const plugin = await createPluginUI({
                target: containerRef.current,
                render: renderReact18,
                layoutIsExpanded: false,
                layoutShowControls: false,
                layoutShowRemoteState: false,
                layoutShowSequence: true,
                layoutShowLog: false,
                layoutShowLeftPanel: false
            });

            if (cancelled) { plugin.dispose(); return; }
            pluginRef.current = plugin;

            const data = await plugin.builders.data.rawData({ data: cifString, label: 'projection' });
            const trajectory = await plugin.builders.structure.parseTrajectory(data, 'mmcif');
            const preset = await plugin.builders.structure.hierarchy.applyPreset(trajectory, 'default');

            cartoonRef.current = preset?.representation?.representations?.polymer || null;

            const structCell = plugin.managers.structure.hierarchy.current.structures[0]?.cell;
            structureRef.current = structCell?.transform?.ref || null;
            structureDataRef.current = structCell?.obj?.data || null;
            indexRef.current = buildStructureIndex(structureDataRef.current);

            if (cartoonRef.current) await resetCartoonColour(plugin, cartoonRef.current);

            if (!cancelled) setReady(true);
        };

        init();

        return () => {
            cancelled = true;
            if (pluginRef.current) { pluginRef.current.dispose(); pluginRef.current = null; }
            cartoonRef.current = null;
            structureRef.current = null;
            structureDataRef.current = null;
            selectionNodesRef.current = [];
            networkRefs.current = [];
            setHasNetwork(false);
            setReady(false);
        };
    }, [cifString]);

    // ------------------------------------------------------------- colours
    useEffect(() => {
        const plugin = pluginRef.current;
        const repRef = cartoonRef.current;
        if (!ready || !plugin || !repRef) return;

        const layers = [];
        const labels = new Map();

        const push = (bucketMap, colour, labelSeq, text) => {
            const auth = toAuth(labelSeq);
            if (!bucketMap.has(colour)) bucketMap.set(colour, []);
            bucketMap.get(colour).push(auth);
            labels.set(auth, text);
        };

        const buckets = new Map();

        if (colourBy === 'map') {
            mapRuns.forEach((run) => {
                const colour = MAP_COLOURS[run.state];
                if (colour === undefined) return;
                for (let p = run.from; p <= run.to; p += 1) {
                    push(buckets, colour, p, `${p} · ${run.state}`);
                }
            });
        } else if (colourBy === 'contacts') {
            Object.keys(perPosition || {}).forEach((key) => {
                const pos = parseInt(key, 10);
                const n = perPosition[key].contacts || 0;
                const colour = contactColour(n);
                if (colour === null) return;
                push(buckets, colour, pos, `${pos} · ${n} contact${n === 1 ? '' : 's'}`);
            });
        } else {
            // Strongest class per residue, so one PTM candidate does not paint
            // over a high-confidence active site on the same position.
            const best = new Map();
            annotations.forEach((row) => {
                if (row.query_pos === null || row.query_pos === undefined) return;
                const rank = CLASS_PRIORITY.indexOf(row.projection_class);
                if (rank < 0) return;
                const current = best.get(row.query_pos);
                if (current === undefined || rank < current.rank) {
                    best.set(row.query_pos, { rank, klass: row.projection_class, label: row.label });
                }
            });
            best.forEach((entry, pos) => {
                push(buckets, CLASS_COLOURS[entry.klass], pos,
                    `${pos} · ${entry.label} · ${entry.klass.replace(/_/g, ' ')}`);
            });
        }

        buckets.forEach((positions, colour) => layers.push({ colour, positions }));

        overpaintResidues(plugin, repRef, layers, chain).catch(() => {});

        if (labelProviderRef.current) {
            plugin.managers.lociLabels.removeProvider(labelProviderRef.current);
        }
        labelProviderRef.current = residueLabelProvider(labels);
        plugin.managers.lociLabels.addProvider(labelProviderRef.current);
    }, [ready, colourBy, annotations, mapRuns, perPosition, chain, toAuth]);

    // ------------------------------------------------- the contact network
    useEffect(() => {
        const plugin = pluginRef.current;
        if (!ready || !plugin || !structureRef.current) return;
        if (networkRefs.current.length) return;

        const build = async () => {
            const { atomsByAuth } = indexRef.current;
            const interactions = [];
            const residues = new Map();

            contacts.forEach((row) => {
                const aAuth = toAuth(toSeqId(row.num_a));
                const bAuth = toAuth(toSeqId(row.num_b));
                const aChain = row.chain_a || chain;
                const bChain = row.chain_b || chain;

                const anchorA = resolveAnchorAtom(atomsByAuth, aChain, aAuth, row.atom_a);
                const anchorB = resolveAnchorAtom(atomsByAuth, bChain, bAuth, row.atom_b);
                if (!anchorA || !anchorB) return;

                interactions.push({
                    kind: 'unknown',
                    aStructureRef: structureRef.current,
                    a: { auth_asym_id: aChain, auth_seq_id: aAuth, ...(anchorA.atom ? { label_atom_id: anchorA.atom } : {}) },
                    bStructureRef: structureRef.current,
                    b: { auth_asym_id: bChain, auth_seq_id: bAuth, ...(anchorB.atom ? { label_atom_id: anchorB.atom } : {}) },
                    description: `${aChain}/${row.num_a} \u2194 ${bChain}/${row.num_b}`
                        + (Number.isFinite(parseFloat(row.distance)) ? ` - ${parseFloat(row.distance).toFixed(2)}\u00c5` : '')
                });
                residues.set(`${aChain}|${aAuth}`, { chain: aChain, seq: aAuth });
                residues.set(`${bChain}|${bAuth}`, { chain: bChain, seq: bAuth });
            });

            if (!interactions.length) return;

            try {
                const update = plugin.build();
                const node = update.toRoot().apply(
                    CustomInteractions,
                    { interactions },
                    { dependsOn: [structureRef.current], label: 'Contact network' }
                );
                node.apply(InteractionsShape, {
                    styles: { unknown: { color: Color(NETWORK_COLOR), style: 'dashed', radius: NETWORK_RADIUS } }
                }).apply(ShapeRepresentation3D);
                await update.commit();
                networkRefs.current.push(node.ref);
            } catch (e) {
                console.warn('Contact network failed:', e);
                return;
            }

            // Sticks for the residues the network touches, so the lines land
            // on visible side chains instead of bare cartoon.
            try {
                const structCell = plugin.managers.structure.hierarchy.current.structures[0]?.cell;
                if (structCell && residues.size) {
                    const expressions = Array.from(residues.values()).map(({ chain: c, seq }) =>
                        MolScriptBuilder.struct.generator.atomGroups({
                            'chain-test': MolScriptBuilder.core.rel.eq([
                                MolScriptBuilder.struct.atomProperty.macromolecular.auth_asym_id(), c
                            ]),
                            'residue-test': MolScriptBuilder.core.rel.eq([
                                MolScriptBuilder.struct.atomProperty.macromolecular.auth_seq_id(), seq
                            ])
                        }));

                    const sel = await plugin.builders.structure.tryCreateComponentFromExpression(
                        structCell,
                        MolScriptBuilder.struct.combinator.merge(expressions),
                        'contact-network-residues',
                        { label: 'Contact network residues' }
                    );
                    if (sel) {
                        const repr = await plugin.builders.structure.representation.addRepresentation(sel, {
                            type: 'ball-and-stick',
                            typeParams: { sizeFactor: 0.16, aspectRatio: 1, ignoreHydrogens: true },
                            color: 'element-symbol',
                            colorParams: {
                                carbonColor: { name: 'uniform', params: { value: Color(NETWORK_CARBON) } },
                                saturation: 0,
                                lightness: 0
                            }
                        });
                        if (repr?.ref) networkRefs.current.push(repr.ref);
                    }
                }
            } catch (e) {
                console.warn('Contact network sticks failed:', e);
            }

            // Built once, then switched with setSubtreeVisibility so the
            // button never rebuilds the scene.
            networkRefs.current.forEach((ref) =>
                setSubtreeVisibility(plugin.state.data, ref, !showNetwork));
            setHasNetwork(networkRefs.current.length > 0);
        };

        build();
    }, [ready, contacts, chain, toAuth, showNetwork]);

    useEffect(() => {
        const plugin = pluginRef.current;
        if (!plugin || !networkRefs.current.length) return;
        networkRefs.current.forEach((ref) =>
            setSubtreeVisibility(plugin.state.data, ref, !showNetwork));
    }, [showNetwork]);

    // ------------------------ what the tables point at: a contact or a residue
    //
    // One effect for both. Two effects meant that clearing a residue selection
    // never ran - the contact effect's dependency had not changed - and the
    // ball-and-stick stayed on screen after "Show whole chain".
    useEffect(() => {
        const plugin = pluginRef.current;
        if (!ready || !plugin) return;

        const run = async () => {
            if (selectionNodesRef.current.length) {
                const update = plugin.build();
                selectionNodesRef.current.forEach((ref) => update.delete(ref));
                await update.commit();
                selectionNodesRef.current = [];
            }

            if (!selectedContact && !selectedResidue) {
                PluginCommands.Camera.Reset(plugin, {});
                return;
            }

            const { atomsByAuth } = indexRef.current;

            // ---- a single residue -------------------------------------
            if (selectedResidue) {
                const auth = toAuth(toSeqId(selectedResidue.query_pos));

                PluginCommands.Toast.Show(plugin, {
                    title: selectedResidue.label || 'Residue',
                    message: `query ${selectedResidue.query_aa || ''}${selectedResidue.query_pos}`
                        + (selectedResidue.human_pos ? ` \u2194 human ${selectedResidue.human_pos}` : '')
                });

                const selection = Script.getStructureSelection((Q) => Q.struct.generator.atomGroups({
                    'chain-test': Q.core.rel.eq([Q.ammp('auth_asym_id'), chain]),
                    'residue-test': Q.core.rel.eq([Q.ammp('auth_seq_id'), auth])
                }), structureDataRef.current);

                try {
                    const reprParams = createStructureRepresentationParams(plugin, undefined, {
                        type: 'ball-and-stick',
                        typeParams: { sizeFactor: 0.25, ignoreHydrogens: true },
                        color: 'element-symbol',
                        colorParams: { carbonColor: { name: 'element-symbol' } }
                    });
                    const update = plugin.build()
                        .to(cartoonRef.current)
                        .apply(StateTransforms.Model.StructureSelectionFromExpression, {
                            label: 'Selected residue',
                            expression: selection
                        });
                    const repr = update.apply(StateTransforms.Representation.StructureRepresentation3D, reprParams);
                    await update.commit();
                    if (repr.ref) selectionNodesRef.current.push(repr.ref);
                } catch (e) {
                    console.warn('Selected residue failed:', e);
                }

                plugin.managers.camera.focusLoci(StructureSelection.toLociWithSourceUnits(selection));
                return;
            }

            // ---- a contact: the pair, plus the interaction line ---------
            const aChain = selectedContact.chain_a || chain;
            const bChain = selectedContact.chain_b || chain;
            const aAuth = toAuth(toSeqId(selectedContact.num_a));
            const bAuth = toAuth(toSeqId(selectedContact.num_b));

            PluginCommands.Toast.Show(plugin, {
                title: 'Contact',
                message: `${aChain}/${selectedContact.num_a}/${selectedContact.res_a}  `
                    + `${bChain}/${selectedContact.num_b}/${selectedContact.res_b} - ${selectedContact.distance}Å`
            });

            const anchorA = resolveAnchorAtom(atomsByAuth, aChain, aAuth, selectedContact.atom_a);
            const anchorB = resolveAnchorAtom(atomsByAuth, bChain, bAuth, selectedContact.atom_b);

            if (structureRef.current && anchorA && anchorB) {
                try {
                    const update = plugin.build();
                    const node = update.toRoot().apply(
                        CustomInteractions,
                        {
                            interactions: [{
                                kind: 'unknown',
                                aStructureRef: structureRef.current,
                                a: { auth_asym_id: aChain, auth_seq_id: aAuth, ...(anchorA.atom ? { label_atom_id: anchorA.atom } : {}) },
                                bStructureRef: structureRef.current,
                                b: { auth_asym_id: bChain, auth_seq_id: bAuth, ...(anchorB.atom ? { label_atom_id: anchorB.atom } : {}) },
                                description: `${aChain}/${selectedContact.num_a}/${selectedContact.res_a} \u2194 `
                                    + `${bChain}/${selectedContact.num_b}/${selectedContact.res_b}`
                                    + ` - ${parseFloat(selectedContact.distance).toFixed(2)}\u00c5`
                            }]
                        },
                        { dependsOn: [structureRef.current], label: 'Selected contact' }
                    );
                    node.apply(InteractionsShape, {
                        styles: {
                            unknown: {
                                color: Color(SELECTED_CONTACT_COLOR),
                                style: 'dashed',
                                radius: SELECTED_CONTACT_RADIUS
                            }
                        }
                    }).apply(ShapeRepresentation3D);
                    await update.commit();
                    selectionNodesRef.current.push(node.ref);
                } catch (e) {
                    console.warn('Selected contact line failed:', e);
                }
            }

            const selection = Script.getStructureSelection((Q) => Q.struct.generator.atomGroups({
                'chain-test': Q.core.set.has([Q.set(aChain, bChain), Q.ammp('auth_asym_id')]),
                'residue-test': Q.core.set.has([Q.set(aAuth, bAuth), Q.ammp('auth_seq_id')])
            }), structureDataRef.current);

            try {
                const reprParams = createStructureRepresentationParams(plugin, undefined, {
                    type: 'ball-and-stick',
                    typeParams: { sizeFactor: 0.2, ignoreHydrogens: true },
                    color: 'element-symbol',
                    colorParams: { carbonColor: { name: 'element-symbol' } }
                });

                const update = plugin.build()
                    .to(cartoonRef.current)
                    .apply(StateTransforms.Model.StructureSelectionFromExpression, {
                        label: 'Selected contact residues',
                        expression: selection
                    });
                const repr = update.apply(StateTransforms.Representation.StructureRepresentation3D, reprParams);
                await update.commit();
                if (repr.ref) selectionNodesRef.current.push(repr.ref);
            } catch (e) {
                console.warn('Selected contact sticks failed:', e);
            }

            plugin.managers.camera.focusLoci(StructureSelection.toLociWithSourceUnits(selection));
        };

        run();
    }, [ready, selectedContact, selectedResidue, chain, toAuth]);

    // ──────────────────────────────────────────────────────────────────────

    return (
        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
            <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />

            <div style={MSP_BUTTON_BAR_STYLE}>
                <button
                    type="button"
                    style={MSP_BUTTON_STYLE}
                    onClick={() => setShowNetwork((v) => !v)}
                    disabled={!hasNetwork}
                >
                    {showNetwork ? 'Hide network' : 'Show network'}
                </button>
            </div>

            {!ready && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: '#FFFFFF'
                    }}
                >
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: '1.6rem', color: '#1F6FB2' }} />
                </div>
            )}
        </div>
    );
};

export default MolstarViewerProjection;
